import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, Brain, Lightbulb, Warning, ArrowRight, CircleNotch, ChartLineUp,
} from "@phosphor-icons/react";

// Marketing Intelligence Phase 5 — Campaign Intelligence tab.
// Reads/POSTs /api/campaigns/{id}/intelligence(/generate). Grounded ONLY in
// real data — backend rejects sparse campaigns with a "not enough data" payload.

const PRIORITY_TONE = {
  High: "bg-rose-400/10 text-rose-200 border-rose-400/30",
  Medium: "bg-amber-400/10 text-amber-200 border-amber-400/30",
  Low: "bg-emerald-400/10 text-emerald-200 border-emerald-400/30",
};

export default function CampaignIntelligence({ campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sparse, setSparse] = useState(null); // {reason, message} when backend rejects

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/intelligence`);
      setData(data);
      setSparse(null);
    } catch {
      toast.error("Couldn't load intelligence");
      setData(null);
    } finally { setLoading(false); }
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!campaignId) return;
    setGenerating(true);
    setSparse(null);
    try {
      const { data: r } = await api.post(`/campaigns/${campaignId}/intelligence/generate`);
      if (r.generated === false) {
        setSparse({ reason: r.reason, message: r.message });
        toast.info("Not enough data to analyze yet");
      } else {
        toast.success(`Analysis generated · ${r.recommendations.length} recommendation${r.recommendations.length === 1 ? "" : "s"}`);
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate analysis");
    } finally { setGenerating(false); }
  };

  if (loading) {
    return <div className="text-sm text-ink-secondary" data-testid="campaign-intelligence-loading">Loading intelligence…</div>;
  }
  if (!data) {
    return <div className="text-sm text-ink-secondary">Intelligence unavailable.</div>;
  }

  const narrative = data.latest_analysis?.narrative;
  const recs = data.recommendations || [];
  const snap = data.data_snapshot || {};
  const hasEnough = data.has_enough_data;

  return (
    <div className="space-y-6" data-testid="campaign-intelligence">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
            <Brain weight="duotone" className="h-3 w-3" /> Intelligence
          </div>
          <p className="mt-2 text-sm text-ink-secondary">
            AI-generated narrative and recommendations grounded strictly in this campaign's real data.
            Nothing is fabricated — if there isn't enough data, the analysis is skipped instead of guessed.
          </p>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="campaign-intelligence-generate"
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating
            ? <><CircleNotch weight="bold" className="h-4 w-4 animate-spin" /> Generating…</>
            : <><Sparkle weight="fill" className="h-4 w-4" /> {narrative ? "Regenerate analysis" : "Generate analysis"}</>}
        </button>
      </div>

      {/* Data snapshot summary — what the AI actually sees */}
      <section
        data-testid="campaign-intelligence-snapshot"
        className="rounded-md border border-white/10 bg-graphite-950/40 p-4 grid grid-cols-2 md:grid-cols-4 gap-3"
      >
        <SnapTile label="Leads" value={snap.funnel?.total_leads || 0} testid="snap-leads" />
        <SnapTile label="Customers" value={snap.roi?.customers_count || 0} testid="snap-customers" />
        <SnapTile label="Revenue" value={fmtMoney(snap.roi?.revenue_total)} testid="snap-revenue" />
        <SnapTile label="Ad spend" value={fmtMoney(snap.marketing_metrics?.spend_total)} testid="snap-spend" />
      </section>

      {sparse ? (
        <SparseState message={sparse.message} />
      ) : !hasEnough && !narrative ? (
        <SparseState message="This campaign doesn't have enough real data yet — no leads, revenue, or marketing metrics have been recorded. Add leads / attribute customers / connect the Metricool inbound API before generating an analysis." />
      ) : narrative ? (
        <>
          <section
            data-testid="campaign-intelligence-narrative"
            className="rounded-md border border-white/10 bg-graphite-900 p-6 space-y-4"
          >
            <NarrativeBlock title="What happened" body={narrative.what_happened} tone="text-ink-primary" testid="narrative-what-happened" />
            <NarrativeBlock title="Why it matters" body={narrative.why_it_matters} tone="text-amber-200/90" testid="narrative-why-it-matters" />
            <NarrativeBlock title="What to do next" body={narrative.what_to_do_next} tone="text-emerald-200" testid="narrative-what-to-do-next" />
            {data.latest_analysis?.created_at && (
              <div className="text-[10px] uppercase tracking-widest text-ink-secondary/70">
                Generated {new Date(data.latest_analysis.created_at).toLocaleString()}
              </div>
            )}
          </section>

          <section data-testid="campaign-intelligence-recommendations" className="space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink-secondary">
              <Lightbulb weight="duotone" className="h-3 w-3" /> Recommendations · {recs.length}
            </div>
            {recs.length === 0 ? (
              <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4 text-sm text-ink-secondary">
                No recommendations yet. Generate an analysis to get 1-3 concrete actions grounded in your data.
              </div>
            ) : (
              <ul className="space-y-2">
                {recs.map((r) => (
                  <li
                    key={r.recommendation_id}
                    data-testid={`campaign-recommendation-${r.recommendation_id}`}
                    className="rounded-md border border-white/10 bg-graphite-900 p-4"
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm text-ink-primary font-medium">{r.title}</div>
                          <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded-sm ${PRIORITY_TONE[r.priority] || PRIORITY_TONE.Medium}`}>
                            {r.priority}
                          </span>
                          <span className="text-[10px] uppercase tracking-widest border border-white/10 bg-graphite-950 px-1.5 py-0.5 rounded-sm text-ink-secondary">
                            {r.status}
                          </span>
                        </div>
                        {r.problem && <div className="mt-2 text-xs text-ink-secondary"><span className="text-ink-primary/80">Problem: </span>{r.problem}</div>}
                        {r.insight && <div className="mt-1 text-xs text-ink-secondary"><span className="text-ink-primary/80">Insight: </span>{r.insight}</div>}
                        {r.recommended_action && <div className="mt-1 text-xs text-ink-secondary"><span className="text-ink-primary/80">Action: </span>{r.recommended_action}</div>}
                        {r.expected_impact && <div className="mt-1 text-xs text-emerald-200/80"><span className="opacity-70">Expected impact: </span>{r.expected_impact}</div>}
                        {r.kpi_ref && <div className="mt-1 text-[10px] uppercase tracking-widest text-ink-secondary">KPI: {r.kpi_ref}</div>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-[11px] text-ink-secondary/70">
              Manage owner, deadline, and status on the <a className="underline hover:text-ink-primary" href="/ai-center/recommendations" target="_blank" rel="noreferrer">Recommendations</a> page.
            </div>
          </section>
        </>
      ) : (
        <div className="rounded-md border border-white/10 bg-graphite-950/40 p-6 text-sm text-ink-secondary" data-testid="campaign-intelligence-empty">
          No analysis yet. Click <span className="text-ink-primary">Generate analysis</span> to run Claude over this campaign's real data.
        </div>
      )}
    </div>
  );
}

function NarrativeBlock({ title, body, tone, testid }) {
  if (!body) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink-secondary">{title}</div>
      <p className={`mt-1 text-sm ${tone}`}>{body}</p>
    </div>
  );
}

function SnapTile({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-white/10 bg-graphite-950 p-3">
      <div className="text-[10px] uppercase tracking-widest text-ink-secondary">{label}</div>
      <div className="mt-1 font-display text-lg tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function SparseState({ message }) {
  return (
    <section
      data-testid="campaign-intelligence-not-enough-data"
      className="rounded-md border border-amber-400/25 bg-amber-400/5 p-6"
    >
      <div className="flex items-start gap-3">
        <Warning weight="duotone" className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
        <div>
          <div className="font-display text-lg tracking-tight text-amber-100">Not enough data yet</div>
          <p className="mt-1 text-sm text-amber-100/80">{message}</p>
          <div className="mt-3 text-[11px] uppercase tracking-widest text-amber-100/60 flex items-center gap-1">
            Analysis skipped to avoid fabricated metrics <ArrowRight weight="bold" className="h-3 w-3" />
          </div>
        </div>
      </div>
    </section>
  );
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "$0";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
